import axios from "axios";
import { getToken } from "./userService";

const WISE_API_BASE_URL = process.env.REACT_APP_WISE_API_URL || "https://api.wise.com";
const WISE_PROFILE_ID = process.env.REACT_APP_WISE_PROFILE_ID; // Your Wise Business profile ID

/**
 * Wise Business API Service
 * Documentation: https://docs.wise.com/api-docs
 * 
 * Setup Instructions:
 * 1. Log into your Wise Business account
 * 2. Go to Settings > API tokens
 * 3. Create a new API token with appropriate permissions
 * 4. Add REACT_APP_WISE_API_TOKEN to your .env file
 * 5. Add REACT_APP_WISE_PROFILE_ID (found in your Wise Business dashboard)
 */

/**
 * Get Wise API token from environment or config
 * In production, this should be stored securely on the backend
 */
function getWiseApiToken() {
  // In production, this should be fetched from your backend
  // The backend should store the Wise API token securely
  return process.env.REACT_APP_WISE_API_TOKEN;
}

/**
 * Create a quote for a transfer
 * @param {Object} quoteData - Quote parameters
 * @param {string} quoteData.sourceCurrency - Source currency (e.g., 'USD')
 * @param {string} quoteData.targetCurrency - Target currency (e.g., 'GBP')
 * @param {number} quoteData.sourceAmount - Amount to transfer
 * @returns {Promise<Object>} Quote details including exchange rate and fees
 */
export async function createWiseQuote(quoteData) {
  try {
    const token = getWiseApiToken();
    if (!token) {
      throw new Error("Wise API token not configured");
    }

    const response = await axios.post(
      `${WISE_API_BASE_URL}/v3/quotes`,
      {
        sourceCurrency: quoteData.sourceCurrency,
        targetCurrency: quoteData.targetCurrency,
        sourceAmount: quoteData.sourceAmount,
        profile: WISE_PROFILE_ID,
      },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error creating Wise quote:", error);
    throw error;
  }
}

/**
 * Create a recipient in Wise
 * @param {Object} recipientData - Recipient details
 * @param {string} recipientData.accountHolderName - Account holder name
 * @param {string} recipientData.country - Country code (e.g., 'GB', 'US')
 * @param {string} recipientData.currency - Currency code
 * @param {string} recipientData.type - Account type ('sort_code', 'aba', 'swift', etc.)
 * @param {Object} recipientData.details - Account details (accountNumber, sortCode, etc.)
 * @returns {Promise<Object>} Created recipient
 */
export async function createWiseRecipient(recipientData) {
  try {
    const token = getWiseApiToken();
    if (!token) {
      throw new Error("Wise API token not configured");
    }

    const response = await axios.post(
      `${WISE_API_BASE_URL}/v1/accounts`,
      {
        currency: recipientData.currency,
        type: recipientData.type,
        profile: WISE_PROFILE_ID,
        accountHolderName: recipientData.accountHolderName,
        legalType: "PRIVATE", // or "BUSINESS"
        details: recipientData.details,
      },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error creating Wise recipient:", error);
    throw error;
  }
}

/**
 * Create a transfer using Wise
 * @param {Object} transferData - Transfer details
 * @param {string} transferData.targetAccount - Recipient account ID
 * @param {string} transferData.quoteUuid - Quote UUID from createWiseQuote
 * @param {string} transferData.reference - Payment reference
 * @returns {Promise<Object>} Transfer details
 */
export async function createWiseTransfer(transferData) {
  try {
    const token = getWiseApiToken();
    if (!token) {
      throw new Error("Wise API token not configured");
    }

    const response = await axios.post(
      `${WISE_API_BASE_URL}/v1/transfers`,
      {
        targetAccount: transferData.targetAccount,
        quoteUuid: transferData.quoteUuid,
        customerTransactionId: transferData.reference,
        details: {
          reference: transferData.reference,
        },
      },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error creating Wise transfer:", error);
    throw error;
  }
}

/**
 * Fund a transfer (move money from your Wise balance)
 * @param {string} transferId - Transfer ID
 * @returns {Promise<Object>} Funding status
 */
export async function fundWiseTransfer(transferId) {
  try {
    const token = getWiseApiToken();
    if (!token) {
      throw new Error("Wise API token not configured");
    }

    const response = await axios.post(
      `${WISE_API_BASE_URL}/v3/profiles/${WISE_PROFILE_ID}/transfers/${transferId}/payments`,
      {
        type: "BALANCE",
      },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error funding Wise transfer:", error);
    throw error;
  }
}

/**
 * Get transfer status
 * @param {string} transferId - Transfer ID
 * @returns {Promise<Object>} Transfer status
 */
export async function getWiseTransferStatus(transferId) {
  try {
    const token = getWiseApiToken();
    if (!token) {
      throw new Error("Wise API token not configured");
    }

    const response = await axios.get(
      `${WISE_API_BASE_URL}/v1/transfers/${transferId}`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error fetching Wise transfer status:", error);
    throw error;
  }
}

/**
 * Validate bank account details before creating recipient
 * This should be called from your backend for security
 * @param {Object} accountDetails - Account details to validate
 * @returns {Promise<Object>} Validation result
 */
export async function validateBankAccount(accountDetails) {
  try {
    // This should be called from your backend API, not directly from frontend
    // The backend should handle the Wise API call
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1"}/payouts/validate-account`,
      accountDetails,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error validating bank account:", error);
    throw error;
  }
}

/**
 * Save payout method to user profile
 * This should store the Wise recipient ID for future payouts
 * @param {Object} payoutData - Payout method data
 * @returns {Promise<Object>} Saved payout method
 */
export async function savePayoutMethod(payoutData) {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1"}/payouts/methods`,
      payoutData,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error saving payout method:", error);
    throw error;
  }
}

/**
 * Get user's saved payout methods
 * @returns {Promise<Array>} List of payout methods
 */
export async function getPayoutMethods() {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1"}/payouts/methods`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error fetching payout methods:", error);
    return [];
  }
}

